/**
 * The frame renderer of desktop floating windows: solid paper/ink windows at
 * screen scale, each focused and closable, resized by the shared window
 * handles, with the item's ⋯ and right-click menu. A window owned by another
 * display shows as a preview.
 */
import { createElement, useMemo } from "react";
import { FrameWindowResizeHandles } from "@/components/focus/focus-window-resize";
import { itemContextMenu } from "@/components/focus/item-context-menu";
import { ItemMenuButton } from "@/components/focus/item-menu";
import type {
  FrameChromeGeometry,
  FrameChromeState,
  FrameRenderer,
} from "@/components/shapes/shared/frame-renderer-context";
import {
  closeFloatingFrame,
  useFloatingFrameStore,
} from "./floating-frame-store";
import { getWindowDisplayId } from "./use-window-role";

const CHROME_INSIDE: FrameChromeGeometry = { inside: true, scale: 1 };

function useFloatingChromeState(id: string): FrameChromeState {
  const maximized = useFloatingFrameStore(
    (state) => state.frames[id]?.mode === "maximized",
  );
  const preview = useFloatingFrameStore(
    (state) => state.frames[id]?.displayId !== getWindowDisplayId(),
  );
  return useMemo(
    () => ({
      focused: true,
      maximized,
      stackCollapsed: false,
      stackOverflowHidden: false,
      preview,
    }),
    [maximized, preview],
  );
}

export const floatingFrameRenderer: FrameRenderer = {
  useChromeState: useFloatingChromeState,
  useChromeGeometry: () => CHROME_INSIDE,
  useBackingScale: () => 1,
  closeFrame: closeFloatingFrame,
  material: "solid",
  independentSurface: true,
  renderControls: (props) => createElement(FrameWindowResizeHandles, props),
  // The item's ⋯ and right-click, without Focus: it is a window already.
  renderMenu: (props) => createElement(ItemMenuButton, props),
  contextMenu: itemContextMenu,
};
